/**
 * Security Utility Functions
 * Centralized security operations with proper validation and error handling
 */

/**
 * Safely performs base64url decoding with validation
 * @param {string} input - Base64url encoded string
 * @returns {string|null} Decoded string or null if invalid
 */
export const safeBase64UrlDecode = (input) => {
  if (!input || typeof input !== 'string') {
    console.error('Invalid input for base64url decoding');
    return null;
  }

  try {
    // Replace URL-safe characters and add padding
    let str = input.replace(/-/g, '+').replace(/_/g, '/');
    while (str.length % 4) {
      str += '=';
    }
    
    return atob(str);
  } catch (error) {
    console.error('Base64url decoding failed:', error);
    return null;
  }
};

/**
 * Safely performs base64url encoding
 * @param {string} input - String to encode
 * @returns {string|null} Encoded string or null if invalid
 */
export const safeBase64UrlEncode = (input) => {
  if (!input || typeof input !== 'string') {
    console.error('Invalid input for base64url encoding');
    return null;
  }

  try {
    return btoa(input)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  } catch (error) {
    console.error('Base64url encoding failed:', error);
    return null;
  }
};

/**
 * Validates JWT token format without decoding
 * @param {string} token - JWT token to validate
 * @returns {boolean} True if format is valid
 */
export const isValidJwtFormat = (token) => {
  if (!token || typeof token !== 'string') {
    return false;
  }

  const parts = token.split('.');
  return parts.length === 2 || parts.length === 3;
};

/**
 * Safely parses JSON with error handling
 * @param {string} jsonString - JSON string to parse
 * @returns {Object|null} Parsed object or null if invalid
 */
export const safeJsonParse = (jsonString) => {
  if (!jsonString || typeof jsonString !== 'string') {
    return null;
  }

  try {
    return JSON.parse(jsonString);
  } catch (error) {
    console.error('JSON parsing failed:', error);
    return null;
  }
};

/**
 * Validates GUID format
 * @param {string} guid - GUID string to validate
 * @returns {boolean} True if valid GUID format
 */
export const isValidGuid = (guid) => {
  if (!guid || typeof guid !== 'string') {
    return false;
  }

  const guidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return guidPattern.test(guid);
};

/**
 * Strips HTML tags using a linear scan to avoid regex backtracking.
 * @param {string} value - Input string that may contain tags.
 * @returns {string} Tag-free string.
 */
const stripHtmlTags = (value) => {
  const segments = [];
  let cursor = 0;

  while (cursor < value.length) {
    const openIndex = value.indexOf('<', cursor);
    if (openIndex === -1) {
      segments.push(value.slice(cursor));
      break;
    }

    if (openIndex > cursor) {
      segments.push(value.slice(cursor, openIndex));
    }

    const closeIndex = value.indexOf('>', openIndex + 1);
    if (closeIndex === -1) {
      segments.push(value.slice(openIndex));
      break;
    }

    cursor = closeIndex + 1;
  }

  return segments.join('');
};

/**
 * Sanitizes user input to prevent XSS and code injection
 * Treats javascript: protocol as equivalent to eval() - NEVER safe
 * @param {string} input - User input to sanitize
 * @returns {string} Sanitized input
 */
export const sanitizeInput = (input) => {
  if (!input || typeof input !== 'string') {
    return '';
  }

  // Strip tags via linear scan before regex replacements.
  const withoutTags = stripHtmlTags(input);

  return withoutTags
    // Remove dangerous protocols (javascript:, data:, vbscript:, etc.)
    .replace(/(?:javascript|data|vbscript|about|file|livescript|mocha):/gi, '')
    // Remove event handlers (onclick, onload, etc.)
    .replace(/on\w+\s*=/gi, '')
    // Remove expression() calls (IE CSS expressions)
    .replace(/expression\s*\(/gi, '')
    // Remove eval() and similar functions - treating javascript: same as eval
    .replace(/(?:eval|setTimeout|setInterval|Function|execScript)/gi, '')
    // Remove dangerous DOM manipulation methods (including 'document' entirely)
    .replace(/(?:innerHTML|outerHTML|document|createElement|write)/gi, '')
    // Remove dangerous network/storage methods 
    .replace(/(?:fetch|XMLHttpRequest|sendBeacon|WebSocket|localStorage|sessionStorage)/gi, '')
    // Remove dangerous property access patterns
    .replace(/\.(?:call|apply|bind)/gi, '')
    // Remove import statements and dynamic imports
    .replace(/import/gi, '')
    // Remove remaining dangerous patterns
    .replace(/(?:atob|btoa)/gi, '')
    // Remove backslashes that could be used for escaping
    .replace(/\\/g, '')
    // Remove quotes that could break out of attributes
    .replace(/['"]/g, '')
    // Remove null bytes used for bypassing
    .replaceAll('\0', '')
    .trim();
};

/**
 * Strict sanitization for text that will be used in HTML attributes
 * @param {string} input - Input to sanitize for HTML attributes
 * @returns {string} Sanitized input safe for HTML attributes
 */
export const sanitizeForHtmlAttribute = (input) => {
  if (!input || typeof input !== 'string') {
    return '';
  }

  // Only allow alphanumeric characters, spaces, hyphens, and underscores
  return input.replace(/[^a-zA-Z0-9\s\-_]/g, '').trim();
};

/**
 * Sanitizes input that will be used in URLs
 * @param {string} input - Input to sanitize for URL usage
 * @returns {string} Sanitized input safe for URLs
 */
export const sanitizeForUrl = (input) => {
  if (!input || typeof input !== 'string') {
    return '';
  }

  // Remove dangerous protocols and encode special characters
  const cleaned = input
    .replace(/(?:javascript|data|vbscript|about|file):/gi, '')
    .replace(/[<>"']/g, '')
    // Remove event handlers that might be in URL parameters
    .replace(/on\w+=/gi, '');

  // Check if the cleaned input contains any remaining dangerous patterns
  // javascript: is equivalent to eval() - reject ANY remaining dangerous content
  const dangerousPatterns = /(?:alert|eval|script|onload|onclick|onerror|malicious|fetch|innerHTML|document|localStorage|sessionStorage|XMLHttpRequest|WebSocket|import|atob|btoa)/i;
  if (dangerousPatterns.test(cleaned)) {
    return '';
  }

  try {
    // Use URL constructor to validate and encode
    const url = new URL(cleaned);
    // Only allow http and https protocols
    if (url.protocol === 'http:' || url.protocol === 'https:') {
      return url.toString();
    }
  } catch (error) {
    // If URL is invalid, return empty string
    return '';
  }

  return '';
};

/**
 * Validates URL format and protocol
 * @param {string} url - URL to validate
 * @param {Array} allowedProtocols - Allowed protocols (default: ['https', 'wss'])
 * @returns {boolean} True if valid and secure URL
 */
export const isSecureUrl = (url, allowedProtocols = ['https', 'wss']) => {
  if (!url || typeof url !== 'string') {
    return false;
  }

  try {
    const urlObj = new URL(url);
    return allowedProtocols.includes(urlObj.protocol.replace(':', ''));
  } catch (error) {
    return false;
  }
};